import { useEffect, useState } from 'react';
import {
  Activity, AlertCircle, AlertTriangle, BarChart2,
  Bell, CheckCircle, ChevronDown, Clock, RefreshCw, Shield,
  ShieldCheck, ShieldOff, Loader2, Timer, TrendingUp,
} from 'lucide-react';
import { useArcStore } from '@/stores/arc-store';
import { useAlarmsStore } from '@/stores/alarms-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArcSubNav } from '@/components/ArcSubNav';
import { cn } from '@/lib/utils';
import type { ArcEvent, CidEventCategory, AlarmEventSeverity } from '@asi/shared';

type ActiveKpi = 'total' | 'alarms' | 'pending' | 'response' | null;

// ─── Constants ────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<CidEventCategory, { label: string; color: string; bar: string }> = {
  alarm:      { label: 'Alarma',           color: 'text-red-500',    bar: 'bg-red-500' },
  restore:    { label: 'Restauración',     color: 'text-green-500',  bar: 'bg-green-500' },
  open_close: { label: 'Apertura/Cierre',  color: 'text-blue-500',   bar: 'bg-blue-500' },
  trouble:    { label: 'Falla',            color: 'text-amber-500',  bar: 'bg-amber-500' },
  test:       { label: 'Prueba',           color: 'text-gray-400',   bar: 'bg-gray-400' },
  system:     { label: 'Sistema',          color: 'text-purple-500', bar: 'bg-purple-500' },
  bypass:     { label: 'Bypass',           color: 'text-orange-500', bar: 'bg-orange-500' },
};

const SEVERITY_CONFIG: Record<AlarmEventSeverity, { label: string; color: string; bg: string }> = {
  CRITICAL: { label: 'Crítico',      color: 'text-red-500',    bg: 'bg-red-500' },
  ALARM:    { label: 'Alarma',       color: 'text-orange-500', bg: 'bg-orange-500' },
  WARNING:  { label: 'Advertencia',  color: 'text-amber-500',  bg: 'bg-amber-500' },
  INFO:     { label: 'Informativo',  color: 'text-blue-500',   bg: 'bg-blue-500' },
};

const SEVERITY_ORDER: AlarmEventSeverity[] = ['CRITICAL', 'ALARM', 'WARNING', 'INFO'];
const CATEGORY_ORDER: CidEventCategory[] = ['alarm', 'trouble', 'open_close', 'bypass', 'restore', 'test', 'system'];

const GT_TIMEZONE = 'America/Guatemala';

function formatResponseTime(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function formatTime(date: string): string {
  return new Date(date).toLocaleTimeString('es-GT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: GT_TIMEZONE,
  });
}

function formatElapsed(received_at: string): string {
  const diff = Math.floor((Date.now() - new Date(received_at).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
}

// ─── KPI Card (clickable) ─────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  valueClass,
  icon: Icon,
  iconClass,
  isActive,
  onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  valueClass?: string;
  icon: typeof AlertCircle;
  iconClass?: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      className={cn(
        'cursor-pointer select-none transition-all hover:shadow-md hover:border-primary/40',
        isActive && 'ring-2 ring-primary border-primary/60 shadow-md',
      )}
      onClick={onClick}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className={cn('text-3xl font-bold tabular-nums', valueClass)}>{value}</div>
            <div className="text-sm text-muted-foreground mt-0.5 truncate">{label}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className={cn('p-2.5 rounded-xl bg-muted/50', iconClass)}>
              <Icon className="h-5 w-5" />
            </div>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground/50 transition-transform duration-200',
                isActive && 'rotate-180 text-primary',
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── KPI Detail Panels ───────────────────────────────────────

function TotalDetailPanel({ events, stats }: {
  events: ArcEvent[];
  stats: { total: number; byCategory: Record<string, number>; bySeverity: Record<string, number>; responseTimeAvg: number | null } | null;
}) {
  const now = Date.now();
  const lastHour  = events.filter((e) => now - new Date(e.received_at).getTime() < 3_600_000).length;
  const last6h    = events.filter((e) => now - new Date(e.received_at).getTime() < 21_600_000).length;
  const last12h   = events.filter((e) => now - new Date(e.received_at).getTime() < 43_200_000).length;

  return (
    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
      {/* Severity breakdown */}
      {SEVERITY_ORDER.map((sev) => {
        const cfg = SEVERITY_CONFIG[sev];
        const count = stats?.bySeverity[sev] ?? 0;
        return (
          <div key={sev} className="flex flex-col gap-1">
            <span className={cn('text-2xl font-bold tabular-nums', cfg.color)}>
              {count.toLocaleString('es-GT')}
            </span>
            <span className="text-xs text-muted-foreground">{cfg.label}</span>
            <div className="h-1 w-full bg-muted rounded-full overflow-hidden mt-1">
              <div
                className={cn('h-full rounded-full', cfg.bg)}
                style={{ width: `${stats && stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        );
      })}
      {/* Time intervals */}
      <div className="col-span-2 md:col-span-4 pt-3 border-t grid grid-cols-3 gap-4">
        <div>
          <span className="text-xl font-bold tabular-nums text-foreground">{lastHour}</span>
          <p className="text-xs text-muted-foreground">Última hora</p>
        </div>
        <div>
          <span className="text-xl font-bold tabular-nums text-foreground">{last6h}</span>
          <p className="text-xs text-muted-foreground">Últimas 6h</p>
        </div>
        <div>
          <span className="text-xl font-bold tabular-nums text-foreground">{last12h}</span>
          <p className="text-xs text-muted-foreground">Últimas 12h</p>
        </div>
      </div>
    </div>
  );
}

function AlarmsDetailPanel({ events }: { events: ArcEvent[] }) {
  const active = events
    .filter((e) => (e.severity === 'CRITICAL' || e.severity === 'ALARM') && !e.acknowledged_at)
    .sort((a, b) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime());

  if (active.length === 0) {
    return (
      <div className="flex items-center gap-3 text-muted-foreground">
        <ShieldCheck className="h-5 w-5 opacity-50" />
        <span className="text-sm">Sin alarmas activas pendientes</span>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">
        {active.length} alarma{active.length !== 1 ? 's' : ''} pendiente{active.length !== 1 ? 's' : ''} — más antiguas primero
      </p>
      <div className="grid grid-cols-1 gap-1.5 max-h-52 overflow-y-auto pr-1 md:grid-cols-2">
        {active.map((e) => {
          const sev = SEVERITY_CONFIG[e.severity];
          return (
            <div key={e.id} className="flex items-center gap-2.5 p-2.5 rounded-lg border bg-card text-sm">
              <div className={cn('w-2 h-2 rounded-full flex-shrink-0 animate-pulse', sev.bg)} />
              <span className="font-mono font-semibold w-12 flex-shrink-0">{e.cid_code}</span>
              <span className="text-muted-foreground flex-1 truncate">{e.zone_name ?? 'Sin zona'}</span>
              <span className={cn('text-xs flex-shrink-0', sev.color)}>{sev.label}</span>
              <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums font-mono">
                {formatElapsed(e.received_at)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendingDetailPanel({ events }: { events: ArcEvent[] }) {
  const pending = events.filter((e) => !e.acknowledged_at);
  const bySev = SEVERITY_ORDER.map((sev) => ({
    sev,
    count: pending.filter((e) => e.severity === sev).length,
  })).filter((r) => r.count > 0);

  const oldest = [...pending]
    .sort((a, b) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime())
    .slice(0, 5);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* By severity */}
      <div>
        <p className="text-xs text-muted-foreground mb-3 font-medium">Pendientes por severidad</p>
        <div className="space-y-2">
          {bySev.map(({ sev, count }) => {
            const cfg = SEVERITY_CONFIG[sev];
            const pct = pending.length > 0 ? (count / pending.length) * 100 : 0;
            return (
              <div key={sev} className="flex items-center gap-3">
                <span className={cn('text-sm w-28 flex-shrink-0', cfg.color)}>{cfg.label}</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div className={cn('h-full rounded-full', cfg.bg)} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-mono tabular-nums w-8 text-right">{count}</span>
              </div>
            );
          })}
          {bySev.length === 0 && <p className="text-sm text-muted-foreground">Sin pendientes</p>}
        </div>
      </div>

      {/* Oldest pending */}
      <div>
        <p className="text-xs text-muted-foreground mb-3 font-medium">Más tiempo sin atender</p>
        <div className="space-y-1.5">
          {oldest.map((e) => {
            const sev = SEVERITY_CONFIG[e.severity];
            return (
              <div key={e.id} className="flex items-center gap-2.5 text-sm">
                <div className={cn('w-2 h-2 rounded-full flex-shrink-0', sev.bg)} />
                <span className="font-mono w-12 flex-shrink-0">{e.cid_code}</span>
                <span className="text-muted-foreground flex-1 truncate">{e.zone_name ?? 'Sin zona'}</span>
                <span className="font-mono text-xs tabular-nums text-amber-500 font-medium">
                  {formatElapsed(e.received_at)}
                </span>
              </div>
            );
          })}
          {oldest.length === 0 && <p className="text-sm text-muted-foreground">Sin pendientes</p>}
        </div>
      </div>
    </div>
  );
}

function ResponseDetailPanel({ events, stats }: {
  events: ArcEvent[];
  stats: { responseTimeAvg: number | null } | null;
}) {
  const measured = events.filter((e) => e.response_time_seconds !== null && e.response_time_seconds > 0);
  const times = measured.map((e) => e.response_time_seconds as number);

  const minTime  = times.length > 0 ? Math.min(...times) : null;
  const maxTime  = times.length > 0 ? Math.max(...times) : null;
  const fast     = times.filter((t) => t < 300).length;
  const normal   = times.filter((t) => t >= 300 && t <= 1800).length;
  const slow     = times.filter((t) => t > 1800).length;
  const distTotal = fast + normal + slow;

  return (
    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
      <div>
        <span className="text-2xl font-bold tabular-nums text-green-500">
          {minTime !== null ? formatResponseTime(minTime) : '—'}
        </span>
        <p className="text-xs text-muted-foreground mt-0.5">Mejor tiempo</p>
      </div>
      <div>
        <span className="text-2xl font-bold tabular-nums text-foreground">
          {stats?.responseTimeAvg != null ? formatResponseTime(Math.round(stats.responseTimeAvg)) : '—'}
        </span>
        <p className="text-xs text-muted-foreground mt-0.5">Promedio</p>
      </div>
      <div>
        <span className="text-2xl font-bold tabular-nums text-red-500">
          {maxTime !== null ? formatResponseTime(maxTime) : '—'}
        </span>
        <p className="text-xs text-muted-foreground mt-0.5">Peor tiempo</p>
      </div>
      <div>
        <span className="text-2xl font-bold tabular-nums text-muted-foreground">
          {measured.length}
        </span>
        <p className="text-xs text-muted-foreground mt-0.5">Mediciones</p>
      </div>

      {distTotal > 0 && (
        <div className="col-span-2 md:col-span-4 pt-3 border-t grid grid-cols-3 gap-4">
          {[
            { label: 'Rápido (< 5min)', count: fast,   color: 'text-green-500', bg: 'bg-green-500' },
            { label: 'Normal (5–30min)', count: normal, color: 'text-blue-500',  bg: 'bg-blue-500'  },
            { label: 'Lento (> 30min)', count: slow,   color: 'text-red-500',   bg: 'bg-red-500'   },
          ].map(({ label, count, color, bg }) => (
            <div key={label}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={cn('w-2 h-2 rounded-full', bg)} />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
              <span className={cn('text-xl font-bold tabular-nums', color)}>{count}</span>
              <p className="text-xs text-muted-foreground">
                {distTotal > 0 ? Math.round((count / distTotal) * 100) : 0}%
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Horizontal Bar Chart ─────────────────────────────────────

function BarRow({ label, count, total, barClass, labelClass }: {
  label: string;
  count: number;
  total: number;
  barClass: string;
  labelClass?: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={cn('text-sm w-36 flex-shrink-0 truncate', labelClass ?? 'text-muted-foreground')}>
        {label}
      </span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', barClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-mono tabular-nums w-10 text-right text-foreground">{count.toLocaleString('es-GT')}</span>
      <span className="text-xs text-muted-foreground w-9 text-right">{pct}%</span>
    </div>
  );
}

// ─── Recent Critical Events ───────────────────────────────────

function RecentCriticalEvents({ events }: {
  events: Array<{
    id: string;
    cid_code: string;
    severity: AlarmEventSeverity;
    zone_name: string | null;
    occurred_at: string;
    received_at: string;
    acknowledged_at: string | null;
    site_id: string | null;
  }>;
}) {
  const recent = events
    .filter((e) => (e.severity === 'CRITICAL' || e.severity === 'ALARM') && !e.acknowledged_at)
    .slice(0, 8);

  if (recent.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
        <ShieldCheck className="h-10 w-10 opacity-40" />
        <p className="text-sm">Sin eventos críticos pendientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {recent.map((event) => {
        const sev = SEVERITY_CONFIG[event.severity];
        return (
          <div key={event.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors">
            <div className={cn('w-2 h-2 rounded-full flex-shrink-0', sev.bg)} />
            <span className="font-mono text-sm font-semibold w-14 flex-shrink-0 text-foreground">
              {event.cid_code}
            </span>
            <span className="text-sm text-muted-foreground flex-1 truncate">
              {event.zone_name ?? '—'}
            </span>
            <span className={cn('text-xs font-medium flex-shrink-0', sev.color)}>
              {sev.label}
            </span>
            <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
              {formatTime(event.received_at)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Panel Status Summary ─────────────────────────────────────

function PanelStatusSummary() {
  const panels = useAlarmsStore((s) => s.panels);

  if (panels.length === 0) return null;

  const online   = panels.filter((p) => p.status === 'ONLINE').length;
  const armed    = panels.filter((p) => p.is_armed).length;
  const disarmed = panels.filter((p) => !p.is_armed && p.status === 'ONLINE').length;
  const offline  = panels.filter((p) => p.status === 'OFFLINE' || p.status === 'FAULT').length;
  const total    = panels.length;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div className="flex flex-col items-center justify-center p-4 rounded-xl border bg-card gap-1">
        <Shield className="h-5 w-5 text-green-500" />
        <span className="text-2xl font-bold tabular-nums text-green-500">{armed}</span>
        <span className="text-xs text-muted-foreground">Armados</span>
      </div>
      <div className="flex flex-col items-center justify-center p-4 rounded-xl border bg-card gap-1">
        <ShieldOff className="h-5 w-5 text-blue-400" />
        <span className="text-2xl font-bold tabular-nums text-blue-400">{disarmed}</span>
        <span className="text-xs text-muted-foreground">Desarmados</span>
      </div>
      <div className="flex flex-col items-center justify-center p-4 rounded-xl border bg-card gap-1">
        <Activity className="h-5 w-5 text-emerald-500" />
        <span className="text-2xl font-bold tabular-nums text-emerald-500">{online}</span>
        <span className="text-xs text-muted-foreground">En línea</span>
      </div>
      <div className="flex flex-col items-center justify-center p-4 rounded-xl border bg-card gap-1">
        <AlertCircle className="h-5 w-5 text-red-400" />
        <span className="text-2xl font-bold tabular-nums text-red-400">{offline}</span>
        <span className="text-xs text-muted-foreground">Sin señal</span>
      </div>
      <div className="col-span-2 sm:col-span-4">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-24">Cobertura</span>
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${total > 0 ? Math.round((online / total) * 100) : 0}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">
            {total > 0 ? Math.round((online / total) * 100) : 0}% ({online}/{total})
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function ArcDashboardPage() {
  const arcStore = useArcStore();
  const alarmsStore = useAlarmsStore();
  const [activeKpi, setActiveKpi] = useState<ActiveKpi>(null);

  useEffect(() => {
    arcStore.fetchStats();
    arcStore.fetchPendingCount();
    arcStore.fetchEvents();
    alarmsStore.fetchPanels();
  }, []);

  const stats  = arcStore.stats;
  const events = arcStore.events;
  const loading = arcStore.loading && events.length === 0;

  const pendingCount   = arcStore.pendingCount ?? events.filter((e) => !e.acknowledged_at).length;
  const resolvedCount  = stats ? Math.max(0, stats.total - pendingCount) : 0;
  const pctAtendidos   = stats && stats.total > 0 ? Math.round((resolvedCount / stats.total) * 100) : 100;
  const critical       = stats?.bySeverity['CRITICAL'] ?? 0;
  const alarm          = stats?.bySeverity['ALARM'] ?? 0;
  const totalCat       = stats ? Object.values(stats.byCategory).reduce((a, b) => a + b, 0) : 0;
  const totalSev       = stats ? Object.values(stats.bySeverity).reduce((a, b) => a + b, 0) : 0;

  function toggleKpi(kpi: ActiveKpi) {
    setActiveKpi((prev) => (prev === kpi ? null : kpi));
  }

  const kpiDetailTitle: Record<NonNullable<ActiveKpi>, string> = {
    total:    'Desglose de eventos',
    alarms:   'Alarmas activas',
    pending:  'Análisis de pendientes',
    response: 'Distribución de tiempos de respuesta',
  };

  return (
    <div className="h-full flex flex-col p-6 overflow-y-auto">
      <ArcSubNav />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart2 className="h-7 w-7" />
            Dashboard ARC
          </h1>
          <p className="text-muted-foreground text-sm">
            Resumen operativo — últimas 24 horas
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { arcStore.fetchStats(); arcStore.fetchPendingCount(); arcStore.fetchEvents(); alarmsStore.fetchPanels(); }}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-1.5">Actualizar</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── KPI Row ──────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard
              label="Total eventos 24h"
              value={(stats?.total ?? 0).toLocaleString('es-GT')}
              icon={Activity}
              iconClass="text-blue-500"
              isActive={activeKpi === 'total'}
              onClick={() => toggleKpi('total')}
            />
            <KpiCard
              label="Alarmas activas"
              value={(critical + alarm).toLocaleString('es-GT')}
              sub={`${critical} críticos · ${alarm} alarma`}
              valueClass={critical + alarm > 0 ? 'text-red-500' : undefined}
              icon={AlertTriangle}
              iconClass={critical + alarm > 0 ? 'text-red-500' : 'text-muted-foreground'}
              isActive={activeKpi === 'alarms'}
              onClick={() => toggleKpi('alarms')}
            />
            <KpiCard
              label="Pendientes ahora"
              value={pendingCount.toLocaleString('es-GT')}
              sub={`${resolvedCount.toLocaleString('es-GT')} atendidos (${pctAtendidos}%)`}
              valueClass={pendingCount > 0 ? 'text-amber-500' : 'text-green-500'}
              icon={pendingCount > 0 ? Bell : CheckCircle}
              iconClass={pendingCount > 0 ? 'text-amber-500' : 'text-green-500'}
              isActive={activeKpi === 'pending'}
              onClick={() => toggleKpi('pending')}
            />
            <KpiCard
              label="Tiempo prom. respuesta"
              value={stats?.responseTimeAvg != null ? formatResponseTime(Math.round(stats.responseTimeAvg)) : '-'}
              icon={Clock}
              iconClass="text-muted-foreground"
              isActive={activeKpi === 'response'}
              onClick={() => toggleKpi('response')}
            />
          </div>

          {/* ── KPI Detail Panel ─────────────────────────────────── */}
          {activeKpi && (
            <Card className="border-primary/30">
              <CardHeader className="pb-3 pt-4">
                <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
                  {activeKpi === 'total'    && <TrendingUp className="h-4 w-4" />}
                  {activeKpi === 'alarms'   && <AlertTriangle className="h-4 w-4 text-red-500" />}
                  {activeKpi === 'pending'  && <Timer className="h-4 w-4 text-amber-500" />}
                  {activeKpi === 'response' && <Clock className="h-4 w-4" />}
                  {kpiDetailTitle[activeKpi]}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {activeKpi === 'total'    && <TotalDetailPanel    events={events} stats={stats} />}
                {activeKpi === 'alarms'   && <AlarmsDetailPanel   events={events} />}
                {activeKpi === 'pending'  && <PendingDetailPanel  events={events} />}
                {activeKpi === 'response' && <ResponseDetailPanel events={events} stats={stats} />}
              </CardContent>
            </Card>
          )}

          {/* ── Distributions + Critical Events ──────────────────── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* By category */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Por categoría</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats ? (
                  CATEGORY_ORDER
                    .filter((cat) => (stats.byCategory[cat] ?? 0) > 0)
                    .sort((a, b) => (stats.byCategory[b] ?? 0) - (stats.byCategory[a] ?? 0))
                    .map((cat) => (
                      <BarRow
                        key={cat}
                        label={CATEGORY_CONFIG[cat].label}
                        count={stats.byCategory[cat] ?? 0}
                        total={totalCat}
                        barClass={CATEGORY_CONFIG[cat].bar}
                        labelClass={CATEGORY_CONFIG[cat].color}
                      />
                    ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin datos</p>
                )}
              </CardContent>
            </Card>

            {/* By severity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Por severidad</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {stats ? (
                  SEVERITY_ORDER
                    .filter((sev) => (stats.bySeverity[sev] ?? 0) > 0)
                    .map((sev) => (
                      <BarRow
                        key={sev}
                        label={SEVERITY_CONFIG[sev].label}
                        count={stats.bySeverity[sev] ?? 0}
                        total={totalSev}
                        barClass={SEVERITY_CONFIG[sev].bg}
                        labelClass={SEVERITY_CONFIG[sev].color}
                      />
                    ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin datos</p>
                )}
              </CardContent>
            </Card>

            {/* Pending critical events */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Alarmas pendientes
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-auto text-xs">
                      {events.filter((e) => !e.acknowledged_at && (e.severity === 'CRITICAL' || e.severity === 'ALARM')).length}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 pb-4 px-4">
                <RecentCriticalEvents events={events} />
              </CardContent>
            </Card>
          </div>

          {/* ── Panel Status ─────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Estado de paneles
                <Badge variant="secondary" className="ml-auto text-xs">
                  {alarmsStore.panels.length} paneles
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alarmsStore.panels.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Cargando estado de paneles...
                </p>
              ) : (
                <PanelStatusSummary />
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ArcDashboardPage;
